import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Download, 
  ExternalLink, 
  Activity,
  Database,
  Shield,
  Users,
  Key,
  FileCheck,
  Mail,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';

/**
 * API Documentation Page
 * 
 * Provides access to:
 * - Swagger UI (embedded iframe)
 * - Permission matrix export
 * - Health check dashboard
 * - System metrics
 */
const APIDocumentationPage = () => {
  const [activeTab, setActiveTab] = useState('swagger');
  const [healthData, setHealthData] = useState(null);
  const [matrixStats, setMatrixStats] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch health check data
  const fetchHealthData = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/health');
      const data = await response.json();
      setHealthData(data);
    } catch (error) {
      console.error('Error fetching health data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch permission matrix stats
  const fetchMatrixStats = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/permissions/matrix/stats', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setMatrixStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching matrix stats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Download permission matrix
  const downloadPermissionMatrix = async (format = 'grouped') => {
    try {
      const response = await fetch(`/api/admin/permissions/matrix/export?format=${format}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        },
      });
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `permission_matrix_${format}_${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading permission matrix:', error);
    }
  };

  useEffect(() => {
    if (activeTab === 'health') {
      fetchHealthData();
    } else if (activeTab === 'matrix') {
      fetchMatrixStats();
    }
  }, [activeTab]);

  // Auto-refresh health data every 30 seconds
  useEffect(() => {
    if (activeTab === 'health') {
      const interval = setInterval(fetchHealthData, 30000);
      return () => clearInterval(interval);
    }
  }, [activeTab]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                API Documentation & Testing
              </h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Comprehensive API documentation, permission matrix, and system health monitoring
              </p>
            </div>
            <FileText className="w-12 h-12 text-indigo-600 dark:text-indigo-400" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('swagger')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'swagger'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <FileText className="w-5 h-5" />
                <span>Swagger UI</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('matrix')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'matrix'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5" />
                <span>Permission Matrix</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('health')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'health'
                  ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Activity className="w-5 h-5" />
                <span>Health Check</span>
              </div>
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Swagger UI Tab */}
        {activeTab === 'swagger' && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    OpenAPI 3.0 Documentation
                  </h2>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Interactive API documentation with request/response examples
                  </p>
                </div>
                <a
                  href="/api/docs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Open in New Tab
                </a>
              </div>
            </div>
            <div className="p-0">
              <iframe
                src="/api/docs"
                className="w-full h-[800px] border-0"
                title="Swagger UI"
              />
            </div>
          </div>
        )}

        {/* Permission Matrix Tab */}
        {activeTab === 'matrix' && (
          <div className="space-y-6">
            {/* Stats Cards */}
            {matrixStats && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Total Roles
                      </p>
                      <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                        {matrixStats.totalRoles}
                      </p>
                    </div>
                    <Users className="w-12 h-12 text-indigo-600 dark:text-indigo-400" />
                  </div>
                </div>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                        Total Permissions
                      </p>
                      <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                        {matrixStats.totalPermissions}
                      </p>
                    </div>
                    <Key className="w-12 h-12 text-green-600 dark:text-green-400" />
                  </div>
                </div>
              </div>
            )}

            {/* Export Options */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                Export Permission Matrix
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                Download the permission matrix as a CSV file. Choose between detailed (all permissions) or grouped (by resource) format.
              </p>
              <div className="flex space-x-4">
                <button
                  onClick={() => downloadPermissionMatrix('grouped')}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Grouped
                </button>
                <button
                  onClick={() => downloadPermissionMatrix('detailed')}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Detailed
                </button>
              </div>
            </div>

            {/* Role Stats Table */}
            {matrixStats && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Permission Statistics by Role
                  </h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Tier
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Granted
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Constrained
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Denied
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Coverage
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {Object.entries(matrixStats.roleStats).map(([roleName, stats]) => (
                        <tr key={roleName}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                            {roleName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            Tier {stats.tier}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400">
                            {stats.granted} ✅
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600 dark:text-yellow-400">
                            {stats.constrained} 🔒
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400">
                            {stats.denied} ❌
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {stats.grantedPercentage}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Health Check Tab */}
        {activeTab === 'health' && healthData && (
          <div className="space-y-6">
            {/* Overall Status */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    System Status
                  </h2>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                    Last checked: {new Date(healthData.timestamp).toLocaleString()}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  {healthData.status === 'healthy' && (
                    <>
                      <CheckCircle className="w-8 h-8 text-green-600" />
                      <span className="text-lg font-semibold text-green-600">Healthy</span>
                    </>
                  )}
                  {healthData.status === 'degraded' && (
                    <>
                      <AlertCircle className="w-8 h-8 text-yellow-600" />
                      <span className="text-lg font-semibold text-yellow-600">Degraded</span>
                    </>
                  )}
                  {healthData.status === 'unhealthy' && (
                    <>
                      <XCircle className="w-8 h-8 text-red-600" />
                      <span className="text-lg font-semibold text-red-600">Unhealthy</span>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Services Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Database */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Database className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Database
                    </h3>
                  </div>
                  {healthData.services.database.status === 'up' ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-600" />
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {healthData.services.database.message}
                </p>
                {healthData.services.database.responseTime && (
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-500">
                    Response time: {healthData.services.database.responseTime}ms
                  </p>
                )}
              </div>

              {/* Redis */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Activity className="w-6 h-6 text-red-600 dark:text-red-400" />
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Redis Cache
                    </h3>
                  </div>
                  {healthData.services.redis.status === 'up' ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : healthData.services.redis.status === 'not_configured' ? (
                    <AlertCircle className="w-6 h-6 text-gray-400" />
                  ) : (
                    <XCircle className="w-6 h-6 text-red-600" />
                  )}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {healthData.services.redis.message}
                </p>
                {healthData.services.redis.responseTime && (
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-500">
                    Response time: {healthData.services.redis.responseTime}ms
                  </p>
                )}
              </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Active Sessions
                </p>
                <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                  {healthData.metrics.activeSessions || 0}
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  System Uptime
                </p>
                <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                  {Math.floor(healthData.metrics.uptime / 3600)}h
                </p>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  Memory Usage
                </p>
                <p className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                  {healthData.metrics.memory.heapUsed}MB
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default APIDocumentationPage;
